import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(" ");
  return String(detail);
};

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("ph_token", data.access_token);
      navigate("/admin");
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border border-white/20 px-4 py-3.5 text-sm text-white placeholder:text-neutral-600 focus:outline-none focus:border-white transition-colors duration-300";

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white flex items-center justify-center px-4" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className="w-full max-w-md"
      >
        <div className="flex justify-center mb-10">
          <img src="/logo.png" alt="Purpose Haul Dispatch" className="h-14 w-auto invert" />
        </div>
        <form onSubmit={submit} className="border border-white/15 bg-[#101010] p-8 sm:p-10 space-y-6" data-testid="admin-login-form">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-1">Admin Portal</p>
            <h1 className="font-display font-bold uppercase tracking-tight text-2xl">Dispatch Login</h1>
          </div>
          <div>
            <label htmlFor="admin-email" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Email</label>
            <input id="admin-email" data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
          </div>
          <div>
            <label htmlFor="admin-password" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 mb-2">Password</label>
            <input id="admin-password" data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} />
          </div>
          {error && (
            <p className="text-sm text-red-400 border border-red-400/30 bg-red-400/10 px-4 py-3" data-testid="admin-login-error">
              {error}
            </p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit-button"
            disabled={loading}
            className="group w-full inline-flex items-center justify-center gap-3 bg-white text-black font-mono text-xs uppercase tracking-[0.25em] px-8 py-4 hover:bg-neutral-300 disabled:opacity-50 transition-colors duration-300"
          >
            {loading ? "Signing in..." : "Sign In"}
            <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </form>
        <a href="/" data-testid="admin-back-to-site-link" className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 hover:text-white transition-colors duration-300">
          <ArrowLeft size={12} /> Back to site
        </a>
      </motion.div>
    </div>
  );
}
